import React, { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useUser } from "@clerk/clerk-react";
import { useOrganizationContext } from "@/contexts/OrganizationContext";
import {
  WindowHeader,
  WindowContent,
  WindowGrid,
  WindowSection,
  WindowSidebar
} from "@/components/ui/window-layout";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Plus,
  Calendar as CalendarIcon,
  User,
  AlertTriangle,
  Clock,
  CheckSquare,
  Target,
  FileText,
  Building2,
  Users,
  Zap,
  Shield,
  TrendingUp,
  Database,
  Eye,
  Save,
  X
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { useTaskManager } from "@/contexts/TaskManager";
import { createTask } from "@/lib/taskClient";
import {useUserProfile} from "@/hooks/useUserProfile.ts"; // ← ДОБАВЛЕНО

interface TaskCreatorWindowProps {
  onTaskCreated?: (task: any) => void;
  onClose?: () => void;
}

const taskTypes = [
  { value: "audit", label: "Healthcare Audit", icon: <Eye className="w-4 h-4" />, color: "text-blue-500" },
  { value: "investigation", label: "Fraud Investigation", icon: <Shield className="w-4 h-4" />, color: "text-red-500" },
  { value: "analysis", label: "Data Analysis", icon: <TrendingUp className="w-4 h-4" />, color: "text-green-500" },
  { value: "review", label: "Case Review", icon: <FileText className="w-4 h-4" />, color: "text-amber-500" },
  { value: "compliance", label: "Compliance Check", icon: <CheckSquare className="w-4 h-4" />, color: "text-purple-500" },
  { value: "ai-ops", label: "AI Operations", icon: <Zap className="w-4 h-4" />, color: "text-cyan-500" },
  { value: "provider", label: "Provider Assessment", icon: <Building2 className="w-4 h-4" />, color: "text-orange-500" },
  { value: "database", label: "Database Task", icon: <Database className="w-4 h-4" />, color: "text-indigo-500" }
];

const priorities = [
  { value: "low", label: "Low Priority", color: "text-green-400", bgColor: "bg-green-500/10" },
  { value: "medium", label: "Medium Priority", color: "text-amber-400", bgColor: "bg-amber-500/10" },
  { value: "high", label: "High Priority", color: "text-red-400", bgColor: "bg-red-500/10" },
  { value: "urgent", label: "Urgent", color: "text-red-500", bgColor: "bg-red-500/20" }
];

const assignees = [
  "Dr. Sarah Chen",
  "Dr. Michael Torres",
  "Jennifer Park",
  "Alex Kumar",
  "David Rodriguez",
  "Lisa Thompson",
  "Mark Johnson",
  "Emma Wilson"
];

const relatedCases = [
  "CASE-2024-001: Medicare Part B Fraud",
  "CASE-2024-002: DME Billing Anomalies",
  "CASE-2024-003: Provider Network Analysis",
  "CASE-2024-004: Prescription Drug Claims",
  "CASE-2024-005: Telemedicine Audit"
];

export default function TaskCreatorWindow({ onTaskCreated, onClose }: TaskCreatorWindowProps) {
  const { addTask } = useTaskManager();
  const { user } = useUser();
  const { currentOrganization } = useOrganizationContext();
  const profileId = useUserProfile().profile?.id

  const [taskData, setTaskData] = useState({
    title: "",
    description: "",
    details: "",
    type: "",
    priority: "medium",
    assignee: "",
    dueDate: undefined as Date | undefined,
    relatedCase: "",
    tags: [] as string[],
    estimatedHours: "",
    notes: ""
  });

  const [newTag, setNewTag] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false); // ← ДОБАВЛЕНО
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!taskData.title.trim() || !taskData.type) {
      toast({
        title: "Validation Error",
        description: "Please fill in the required fields (Title and Task Type)",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const clerkUserId = user?.id;
      const organizationId = currentOrganization?.id;

      console.log('Creating task with:', {
        clerkUserId,
        organizationId,
        title: taskData.title
      });

      const savedTask = await createTask({
        title: taskData.title,
        description: taskData.description,
        priority: taskData.priority as 'low' | 'medium' | 'high' | 'urgent',
        due_time: taskData.dueDate ? format(taskData.dueDate, "h:mm a") : undefined,
        category: taskTypes.find(t => t.value === taskData.type)?.label,
        assignee: taskData.assignee,
        progress: 0,
        details: taskData.details,
        related_case: taskData.relatedCase,
        tags: taskData.tags,
        estimated_hours: taskData.estimatedHours ? parseFloat(taskData.estimatedHours) : undefined,
        user_id: profileId,
      }, organizationId, clerkUserId);

      const newTask = {
        id: savedTask.id,
        title: savedTask.title,
        description: savedTask.description || '',
        priority: savedTask.priority as "high" | "medium" | "low" | "urgent",
        dueTime: savedTask.due_time || 'No due date',
        category: savedTask.category || 'General',
        assignee: savedTask.assignee,
        progress: savedTask.progress,
        details: savedTask.details || '',
        relatedCase: savedTask.related_case,
        tags: savedTask.tags || [],
        estimatedHours: savedTask.estimated_hours?.toString(),
        notes: savedTask.notes,
        type: taskData.type,
        createdAt: new Date(savedTask.created_at),
        timestamp: new Date(savedTask.created_at),
        user_id: profileId
      };

      await addTask(newTask);

      onTaskCreated?.(newTask);

      toast({
        title: "Task Created Successfully!",
        description: `"${taskData.title}" has been saved to the database`
      });

      onClose?.();

      setTaskData({
        title: "",
        description: "",
        details: "",
        type: "",
        priority: "medium",
        assignee: "",
        dueDate: undefined,
        relatedCase: "",
        tags: [],
        estimatedHours: "",
        notes: ""
      });

    } catch (error) {
      console.error('Error creating task:', error);
      toast({
        title: "Error Creating Task",
        description: error instanceof Error ? error.message : "Failed to create task. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false); // ← ДОБАВЛЕНО
    }
  };

  const addTag = () => {
    if (newTag.trim() && !taskData.tags.includes(newTag.trim())) {
      setTaskData(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()]
      }));
      setNewTag("");
    }
  };

  const removeTag = (tagToRemove: string) => {
    setTaskData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const selectedTaskType = taskTypes.find(t => t.value === taskData.type);
  const selectedPriority = priorities.find(p => p.value === taskData.priority);

  return (
      <div className="h-full flex flex-col bg-transparent">
        <WindowHeader
            title="Create New Task"
            subtitle="Add a new task to your workflow"
            icon={<Plus className="w-6 h-6" />}
            actions={
              <Button variant="ghost" size="sm" onClick={onClose} disabled={isSubmitting}>
                <X className="w-4 h-4" />
              </Button>
            }
        />

        <WindowContent>
          <form onSubmit={handleSubmit}>
            <div style={{ position: 'relative', zIndex: 1 }}>
              <WindowGrid cols={3}>
                {/* Main Form */}
                <div className="lg:col-span-2 space-y-6">
                  {/* Basic Information */}
                  <WindowSection title="Task Information" icon={<FileText className="w-5 h-5" />} variant="strong">
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="title" className="text-foreground font-medium">
                            Task Title <span className="text-red-400">*</span>
                          </Label>
                          <Input
                              id="title"
                              value={taskData.title}
                              onChange={(e) => setTaskData(prev => ({ ...prev, title: e.target.value }))}
                              placeholder="Enter task title..."
                              className="bg-white/5 border-white/10 mt-2"
                              required
                              disabled={isSubmitting}
                          />
                        </div>

                        <div>
                          <Label htmlFor="type" className="text-foreground font-medium">
                            Task Type <span className="text-red-400">*</span>
                          </Label>
                          <Select
                              value={taskData.type}
                              onValueChange={(value) => {
                                console.log('Task type selected:', value);
                                setTaskData(prev => ({ ...prev, type: value }));
                              }}
                              disabled={isSubmitting}
                          >
                            <SelectTrigger className="bg-white/5 border-white/10 mt-2">
                              <SelectValue placeholder="Select task type..." />
                            </SelectTrigger>
                            <SelectContent
                                className="bg-black/90 border-white/20 backdrop-blur-xl z-[9999]"
                                side="bottom"
                                align="start"
                            >
                              {taskTypes.map((type) => (
                                  <SelectItem key={type.value} value={type.value} className="text-foreground hover:bg-white/10">
                                    <div className="flex items-center gap-2">
                                      <span className={type.color}>{type.icon}</span>
                                      {type.label}
                                    </div>
                                  </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="description" className="text-foreground font-medium">Description</Label>
                        <Input
                            id="description"
                            value={taskData.description}
                            onChange={(e) => setTaskData(prev => ({ ...prev, description: e.target.value }))}
                            placeholder="Brief description of the task..."
                            className="bg-white/5 border-white/10 mt-2"
                            disabled={isSubmitting}
                        />
                      </div>

                      <div>
                        <Label htmlFor="details" className="text-foreground font-medium">Detailed Instructions</Label>
                        <Textarea
                            id="details"
                            value={taskData.details}
                            onChange={(e) => setTaskData(prev => ({ ...prev, details: e.target.value }))}
                            placeholder="Provide detailed instructions and context for this task..."
                            className="bg-white/5 border-white/10 mt-2 min-h-24"
                            disabled={isSubmitting}
                        />
                      </div>
                    </div>
                  </WindowSection>

                  {/* Assignment & Scheduling */}
                  <WindowSection title="Assignment & Scheduling" icon={<Users className="w-5 h-5" />} variant="default">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="assignee" className="text-foreground font-medium">Assignee</Label>
                        <Select
                            value={taskData.assignee}
                            onValueChange={(value) => setTaskData(prev => ({ ...prev, assignee: value }))}
                            disabled={isSubmitting}
                        >
                          <SelectTrigger className="bg-white/5 border-white/10 mt-2">
                            <SelectValue placeholder="Select assignee..." />
                          </SelectTrigger>
                          <SelectContent
                              className="bg-black/90 border-white/20 backdrop-blur-xl z-[9999]"
                              side="bottom"
                              align="start"
                          >
                            {assignees.map((assignee) => (
                                <SelectItem key={assignee} value={assignee} className="text-foreground hover:bg-white/10">
                                  <div className="flex items-center gap-2">
                                    <User className="w-4 h-4" />
                                    {assignee}
                                  </div>
                                </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label className="text-foreground font-medium">Due Date</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                                variant="outline"
                                className={cn(
                                    "w-full justify-start text-left font-normal bg-white/5 border-white/10 mt-2",
                                    !taskData.dueDate && "text-muted-foreground"
                                )}
                                disabled={isSubmitting}
                            >
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {taskData.dueDate ? format(taskData.dueDate, "PPP") : "Select due date"}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent
                              className="w-auto p-0 bg-black/90 border-white/20 backdrop-blur-xl z-[9999]"
                              align="start"
                          >
                            <Calendar
                                mode="single"
                                selected={taskData.dueDate}
                                onSelect={(date) => setTaskData(prev => ({ ...prev, dueDate: date }))}
                                initialFocus
                                className="p-3 pointer-events-auto"
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                    </div>
                  </WindowSection>

                  {/* Tags & Additional Info */}
                  <WindowSection title="Additional Information" icon={<Target className="w-5 h-5" />}>
                    <div className="space-y-4">
                      <div>
                        <Label className="text-foreground font-medium">Tags</Label>
                        <div className="flex gap-2 mt-2">
                          <Input
                              value={newTag}
                              onChange={(e) => setNewTag(e.target.value)}
                              placeholder="Add a tag..."
                              className="bg-white/5 border-white/10"
                              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                              disabled={isSubmitting}
                          />
                          <Button type="button" onClick={addTag} size="sm" disabled={isSubmitting}>
                            Add
                          </Button>
                        </div>
                        {taskData.tags.length > 0 && (
                            <div className="flex flex-wrap gap-2 mt-3">
                              {taskData.tags.map((tag) => (
                                  <Badge key={tag} variant="outline" className="bg-white/5 border-white/10">
                                    {tag}
                                    <button
                                        type="button"
                                        onClick={() => removeTag(tag)}
                                        className="ml-2 hover:text-red-400"
                                        disabled={isSubmitting}
                                    >
                                      <X className="w-3 h-3" />
                                    </button>
                                  </Badge>
                              ))}
                            </div>
                        )}
                      </div>

                      <div>
                        <Label htmlFor="notes" className="text-foreground font-medium">Additional Notes</Label>
                        <Textarea
                            id="notes"
                            value={taskData.notes}
                            onChange={(e) => setTaskData(prev => ({ ...prev, notes: e.target.value }))}
                            placeholder="Any additional notes or context..."
                            className="bg-white/5 border-white/10 mt-2"
                            disabled={isSubmitting}
                        />
                      </div>
                    </div>
                  </WindowSection>
                </div>

                {/* Sidebar */}
                <WindowSidebar>
                  {/* Task Summary */}
                  <WindowSection title="Task Summary">
                    <div className="space-y-3">
                      {selectedTaskType && (
                          <div className="flex items-center gap-2 p-2 rounded-lg bg-white/5">
                            <span className={selectedTaskType.color}>{selectedTaskType.icon}</span>
                            <span className="text-sm font-medium">{selectedTaskType.label}</span>
                          </div>
                      )}

                      {selectedPriority && (
                          <div className={cn("flex items-center gap-2 p-2 rounded-lg", selectedPriority.bgColor)}>
                            <AlertTriangle className={cn("w-4 h-4", selectedPriority.color)} />
                            <span className={cn("text-sm font-medium", selectedPriority.color)}>
                        {selectedPriority.label}
                      </span>
                          </div>
                      )}

                      {taskData.assignee && (
                          <div className="flex items-center gap-2 p-2 rounded-lg bg-white/5">
                            <User className="w-4 h-4 text-muted-foreground" />
                            <span className="text-sm font-medium">{taskData.assignee}</span>
                          </div>
                      )}

                      {taskData.dueDate && (
                          <div className="flex items-center gap-2 p-2 rounded-lg bg-white/5">
                            <Clock className="w-4 h-4 text-muted-foreground" />
                            <span className="text-sm font-medium">{format(taskData.dueDate, "PPP")}</span>
                          </div>
                      )}
                    </div>
                  </WindowSection>

                  {/* Priority Selection */}
                  <WindowSection title="Priority Level">
                    <div className="space-y-2">
                      {priorities.map((priority) => (
                          <button
                              key={priority.value}
                              type="button"
                              onClick={() => setTaskData(prev => ({ ...prev, priority: priority.value }))}
                              className={cn(
                                  "w-full flex items-center gap-2 p-2 rounded-lg text-left transition-colors",
                                  taskData.priority === priority.value
                                      ? cn(priority.bgColor, "border border-white/20")
                                      : "bg-white/5 hover:bg-white/10"
                              )}
                              disabled={isSubmitting}
                          >
                            <AlertTriangle className={cn("w-4 h-4", priority.color)} />
                            <span className={cn("text-sm font-medium", priority.color)}>
                        {priority.label}
                      </span>
                          </button>
                      ))}
                    </div>
                  </WindowSection>

                  {/* Related Case */}
                  <WindowSection title="Related Case">
                    <Select
                        value={taskData.relatedCase}
                        onValueChange={(value) => setTaskData(prev => ({ ...prev, relatedCase: value }))}
                        disabled={isSubmitting}
                    >
                      <SelectTrigger className="bg-white/5 border-white/10">
                        <SelectValue placeholder="Link to case..." />
                      </SelectTrigger>
                      <SelectContent
                          className="bg-black/90 border-white/20 backdrop-blur-xl z-[9999]"
                          side="bottom"
                          align="start"
                      >
                        {relatedCases.map((caseItem) => (
                            <SelectItem key={caseItem} value={caseItem} className="text-foreground hover:bg-white/10">
                              {caseItem}
                            </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </WindowSection>

                  {/* Estimated Hours */}
                  <WindowSection title="Time Estimate">
                    <div>
                      <Label htmlFor="hours" className="text-foreground font-medium text-sm">Estimated Hours</Label>
                      <Input
                          id="hours"
                          type="number"
                          value={taskData.estimatedHours}
                          onChange={(e) => setTaskData(prev => ({ ...prev, estimatedHours: e.target.value }))}
                          placeholder="Hours"
                          className="bg-white/5 border-white/10 mt-2"
                          min="0"
                          step="0.5"
                          disabled={isSubmitting}
                      />
                    </div>
                  </WindowSection>
                </WindowSidebar>
              </WindowGrid>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-white/10">
              <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button type="submit" className="flex items-center gap-2" disabled={isSubmitting}>
                <Save className="w-4 h-4" />
                {isSubmitting ? 'Creating...' : 'Create Task'}
              </Button>
            </div>
          </form>
        </WindowContent>
      </div>
  );
}